import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { Clock } from 'lucide-react';

interface InvoiceEditHistoryDialogProps {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InvoiceEditHistoryDialog = ({ invoiceId, open, onOpenChange }: InvoiceEditHistoryDialogProps) => {
  const { data: history = [], isLoading } = useQuery({
    queryKey: ['invoice-edit-history', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_edit_history')
        .select(`
          *,
          editor:edited_by(
            first_name,
            last_name,
            email
          )
        `)
        .eq('invoice_id', invoiceId)
        .order('edited_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const renderChanges = (changes: any) => {
    if (!changes || typeof changes !== 'object') return 'No specific changes tracked';
    const changedFields = [];
    if ((changes as any)?.customer_changed) changedFields.push('Customer');
    if ((changes as any)?.dates_changed) changedFields.push('Dates');
    if ((changes as any)?.amounts_changed) changedFields.push('Amounts');
    if ((changes as any)?.notes_changed) changedFields.push('Notes');
    if ((changes as any)?.status_changed) changedFields.push('Status');
    
    return changedFields.length > 0 ? changedFields.join(', ') : 'No specific changes tracked';
  };

  const getEditorName = (editor: any) => {
    if (!editor) return 'Unknown User';
    if (editor.first_name && editor.last_name) {
      return `${editor.first_name} ${editor.last_name}`;
    }
    return editor.email || 'Unknown User';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Invoice Edit History
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh]">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading history...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No edit history found</div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => (
                <div key={entry.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {format(new Date(entry.edited_at), 'MMM d, yyyy h:mm a')}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        by {getEditorName(entry.editor)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Changes Made:</div>
                    <div className="text-sm text-muted-foreground">
                      {renderChanges(entry.changes)}
                    </div>
                  </div>

                  {entry.previous_values && entry.new_values && typeof entry.previous_values === 'object' && typeof entry.new_values === 'object' && (
                    <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t text-sm">
                      <div>
                        <div className="font-medium mb-2">Previous Values:</div>
                        <div className="space-y-1 text-muted-foreground">
                          {(entry.changes as any)?.dates_changed && (
                            <>
                              <div>Invoice Date: {(entry.previous_values as any).invoice_date}</div>
                              <div>Due Date: {(entry.previous_values as any).due_date}</div>
                            </>
                          )}
                          {(entry.changes as any)?.amounts_changed && (
                            <>
                              <div>Subtotal: ${Number((entry.previous_values as any).subtotal || 0).toFixed(2)}</div>
                              <div>Total: ${Number((entry.previous_values as any).total_amount || 0).toFixed(2)}</div>
                            </>
                          )}
                          {(entry.changes as any)?.status_changed && (
                            <div>Status: {(entry.previous_values as any).status}</div>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="font-medium mb-2">New Values:</div>
                        <div className="space-y-1 text-muted-foreground">
                          {(entry.changes as any)?.dates_changed && (
                            <>
                              <div>Invoice Date: {(entry.new_values as any).invoice_date}</div>
                              <div>Due Date: {(entry.new_values as any).due_date}</div>
                            </>
                          )}
                          {(entry.changes as any)?.amounts_changed && (
                            <>
                              <div>Subtotal: ${Number((entry.new_values as any).subtotal || 0).toFixed(2)}</div>
                              <div>Total: ${Number((entry.new_values as any).total_amount || 0).toFixed(2)}</div>
                            </>
                          )}
                          {(entry.changes as any)?.status_changed && (
                            <div>Status: {(entry.new_values as any).status}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
